// VoiceCloningSection.js — "The AI Behind Every Dub"
// Cinematic scroll-driven pipeline: 7 AI stages, monochrome, particle background.
// Sticky pin; each stage has a glass capsule and a bespoke canvas micro-animation.

import { useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, useScroll, AnimatePresence } from 'framer-motion';

const STAGES = [
  { id: 'input',       label: 'Input Voice',           meta: '16.2s · English' },
  { id: 'embedding',   label: 'Speaker Embedding',     meta: 'Voice fingerprint' },
  { id: 'translation', label: 'Translation Engine',    meta: 'Semantic decode' },
  { id: 'prosody',     label: 'Prosody Transfer',      meta: 'Rhythm · Cadence' },
  { id: 'emotion',     label: 'Emotion Preservation',  meta: 'Affect intact' },
  { id: 'lipsync',     label: 'Lip Sync Generation',   meta: 'Phoneme aligned' },
  { id: 'output',      label: 'Final Dubbed Voice',    meta: 'Ready · 32 languages' },
];

// Scroll windows per stage (start, end); the tail after the last stage is the outro.
const INTRO_END   = 0.06;
const STAGE_SPAN  = 0.12;
const STAGE_WINDOWS = STAGES.map((_, i) => {
  const start = INTRO_END + i * STAGE_SPAN;
  return [start, start + STAGE_SPAN];
});
const OUTRO_START = INTRO_END + STAGES.length * STAGE_SPAN; // 0.90

export default function VoiceCloningSection() {
  const outerRef        = useRef(null);
  const neuralCanvasRef = useRef(null);
  const microCanvasRef  = useRef(null);
  const progressRef     = useRef(0);
  const prevActiveRef   = useRef(-1);

  const [activeIndex, setActiveIndex] = useState(-1);

  const { scrollYProgress } = useScroll({ target: outerRef });

  // Sync scroll → progressRef + activeIndex (throttled by ref compare)
  useEffect(() => {
    return scrollYProgress.on('change', (p) => {
      progressRef.current = p;
      let idx = -1;
      if (p >= INTRO_END) {
        for (let i = 0; i < STAGES.length; i++) {
          const [s, e] = STAGE_WINDOWS[i];
          if (p >= s && p < e) { idx = i; break; }
        }
        if (idx === -1 && p >= OUTRO_START) idx = STAGES.length - 1;
      }
      if (idx !== prevActiveRef.current) {
        prevActiveRef.current = idx;
        setActiveIndex(idx);
      }
    });
  }, [scrollYProgress]);

  // Neural network background — very faint particles + proximity connections
  useEffect(() => {
    const canvas = neuralCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width  = canvas.offsetWidth  * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const COUNT = 120;
    const particles = Array.from({ length: COUNT }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.15 * dpr,
      vy: (Math.random() - 0.5) * 0.15 * dpr,
    }));

    let rafId;
    function draw() {
      const ctx = canvas.getContext('2d');
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
      }

      const threshold = 130 * dpr;
      ctx.lineWidth = 0.5 * dpr;
      for (let i = 0; i < COUNT; i++) {
        for (let j = i + 1; j < COUNT; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d2 = dx * dx + dy * dy;
          if (d2 < threshold * threshold) {
            const a = (1 - Math.sqrt(d2) / threshold) * 0.035;
            ctx.strokeStyle = `rgba(255,255,255,${a})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      ctx.fillStyle = 'rgba(255,255,255,0.14)';
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 0.8 * dpr, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(draw);
    }
    rafId = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
  }, []);

  // Micro-animation canvas — dispatches to a stage-specific renderer
  useEffect(() => {
    const canvas = microCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width  = canvas.offsetWidth  * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let rafId;
    let startT = null;
    function draw(ts) {
      if (startT === null) startT = ts;
      const t = (ts - startT) / 1000;

      const ctx = canvas.getContext('2d');
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      const cx = W / 2;
      const cy = H / 2;
      const stageIdx = prevActiveRef.current;
      const p = progressRef.current;

      let stageP = 0;
      if (stageIdx >= 0 && stageIdx < STAGES.length) {
        const [s, e] = STAGE_WINDOWS[stageIdx];
        stageP = Math.min(1, Math.max(0, (p - s) / (e - s)));
      }
      const isOutro = p >= OUTRO_START;

      switch (STAGES[stageIdx]?.id) {
        case 'input':       drawInputWave(ctx, cx, cy, W, H, dpr, t, stageP); break;
        case 'embedding':   drawEmbedding(ctx, cx, cy, W, H, dpr, t, stageP); break;
        case 'translation': drawTranslation(ctx, cx, cy, W, H, dpr, t, stageP); break;
        case 'prosody':     drawProsody(ctx, cx, cy, W, H, dpr, t, stageP); break;
        case 'emotion':     drawEmotion(ctx, cx, cy, W, H, dpr, t, stageP); break;
        case 'lipsync':     drawLipSync(ctx, cx, cy, W, H, dpr, t, stageP); break;
        case 'output':      drawOutput(ctx, cx, cy, W, H, dpr, t, stageP, isOutro); break;
        default: break;
      }

      rafId = requestAnimationFrame(draw);
    }
    rafId = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
  }, []);

  return (
    <Box ref={outerRef} sx={{ height: '260vh', position: 'relative', background: '#000' }}>
      <Box sx={{
        position: 'sticky', top: 0, height: '100vh',
        background: '#000', overflow: 'hidden',
      }}>
        {/* Neural network background */}
        <canvas
          ref={neuralCanvasRef}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            zIndex: 1, opacity: 0.55,
          }}
        />
        {/* Radial vignette to focus attention */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.7) 90%)',
          pointerEvents: 'none',
        }} />

        {/* Content */}
        <Box sx={{
          position: 'relative', zIndex: 2, height: '100%',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          px: { xs: 3, md: 6 }, py: { xs: 5, md: 6 },
        }}>
          {/* Heading */}
          <Box sx={{ textAlign: 'center', maxWidth: 780, mb: { xs: 4, md: 5 } }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.65rem',
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.3)', mb: 2,
            }}>
              03 — Voice Intelligence
            </Typography>
            <Typography component="h2" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(1.9rem, 5vw, 4.2rem)',
              color: '#fff', lineHeight: 1.02, letterSpacing: '-0.045em',
              mb: 2.5,
            }}>
              The AI Behind<br />Every Dub
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(0.82rem, 1.3vw, 1rem)',
              color: 'rgba(255,255,255,0.45)', lineHeight: 1.55,
              maxWidth: 600, mx: 'auto',
            }}>
              Every dubbed voice passes through multiple AI models that preserve identity,
              timing, emotion, and natural speech before generating the final output.
            </Typography>
          </Box>

          {/* Pipeline: capsule column ⟷ micro-animation canvas */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '340px 1fr' },
            gap: { xs: 4, md: 8 },
            alignItems: 'center',
            width: '100%',
            maxWidth: 900,
          }}>
            {/* Capsule column */}
            <Box sx={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center',
            }}>
              {STAGES.map((stage, i) => {
                const isActive   = i === activeIndex;
                const isComplete = activeIndex > i;
                return (
                  <Box key={stage.id} sx={{
                    width: '100%',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                  }}>
                    <StageCapsule
                      label={stage.label}
                      meta={stage.meta}
                      isActive={isActive}
                      isComplete={isComplete}
                    />
                    {i < STAGES.length - 1 && (
                      <ConnectionLine
                        isActive={isActive}
                        isComplete={isComplete}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Micro-animation viewport */}
            <Box sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
              maxWidth: 420,
              maxHeight: '52vh',
              mx: 'auto',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '4px',
              backdropFilter: 'blur(24px)',
              background: 'rgba(255,255,255,0.015)',
              overflow: 'hidden',
              boxShadow: 'inset 0 0 60px rgba(255,255,255,0.02)',
            }}>
              <canvas
                ref={microCanvasRef}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              />
              {/* Corner brackets */}
              <CornerBrackets />
              {/* Stage label */}
              <AnimatePresence mode="wait">
                {activeIndex >= 0 && (
                  <motion.div
                    key={STAGES[activeIndex].id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      position: 'absolute',
                      bottom: 18, left: 18, right: 18,
                      textAlign: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography sx={{
                      fontFamily: 'Inter', fontWeight: 500,
                      fontSize: '0.6rem', letterSpacing: '0.24em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.4)',
                    }}>
                      Processing · 0{activeIndex + 1} / 0{STAGES.length}
                    </Typography>
                    <Typography sx={{
                      fontFamily: 'Inter', fontWeight: 700,
                      fontSize: '0.85rem', letterSpacing: '0.02em',
                      color: '#fff', mt: 0.5,
                    }}>
                      {STAGES[activeIndex].label}
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Capsule ──────────────────────────────────────────────────────────────
function StageCapsule({ label, meta, isActive, isComplete }) {
  return (
    <motion.div
      initial={false}
      animate={{
        opacity: isActive ? 1 : isComplete ? 0.42 : 0.16,
        scale:   isActive ? 1 : 0.95,
        y:       isComplete ? -2 : 0,
      }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{ width: '100%', maxWidth: 320 }}
    >
      <Box sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: isActive ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.12)',
        borderRadius: '999px',
        px: 2.8, py: 1.2,
        backdropFilter: 'blur(12px)',
        background: 'rgba(255,255,255,0.02)',
        boxShadow: isActive ? '0 0 40px rgba(255,255,255,0.07)' : 'none',
        transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        {isActive && (
          <motion.div
            initial={{ opacity: 0.55, scale: 1 }}
            animate={{ opacity: 0, scale: 1.08 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.32)',
              pointerEvents: 'none',
            }}
          />
        )}
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 700,
          fontSize: '0.68rem', letterSpacing: '0.2em',
          textTransform: 'uppercase', color: '#fff',
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 400,
          fontSize: '0.58rem', letterSpacing: '0.08em',
          color: 'rgba(255,255,255,0.45)', mt: 0.2,
        }}>
          {meta}
        </Typography>
      </Box>
    </motion.div>
  );
}

// ─── Vertical connection line ─────────────────────────────────────────────
function ConnectionLine({ isActive, isComplete }) {
  return (
    <Box sx={{
      position: 'relative',
      width: 1,
      height: 14,
      background: isComplete || isActive ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.08)',
      transition: 'background 0.5s ease',
      overflow: 'hidden',
    }}>
      {isActive && (
        <motion.div
          initial={{ y: -6 }}
          animate={{ y: 20 }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            left: -1,
            width: 3, height: 6,
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '2px',
            boxShadow: '0 0 6px rgba(255,255,255,0.6)',
          }}
        />
      )}
    </Box>
  );
}

// ─── Corner brackets for the micro-canvas viewport ────────────────────────
function CornerBrackets() {
  const SIZE = 14;
  const OFFSET = 8;
  const bracketSx = {
    position: 'absolute',
    width: SIZE, height: SIZE,
    borderColor: 'rgba(255,255,255,0.22)',
    zIndex: 3,
    pointerEvents: 'none',
  };
  return (
    <>
      <Box sx={{ ...bracketSx, top: OFFSET, left: OFFSET,
        borderTop: '1px solid', borderLeft: '1px solid' }} />
      <Box sx={{ ...bracketSx, top: OFFSET, right: OFFSET,
        borderTop: '1px solid', borderRight: '1px solid' }} />
      <Box sx={{ ...bracketSx, bottom: OFFSET, left: OFFSET,
        borderBottom: '1px solid', borderLeft: '1px solid' }} />
      <Box sx={{ ...bracketSx, bottom: OFFSET, right: OFFSET,
        borderBottom: '1px solid', borderRight: '1px solid' }} />
    </>
  );
}

// ─── Stage micro-animations ───────────────────────────────────────────────

function drawInputWave(ctx, cx, cy, W, H, dpr, t, sp) {
  const alpha = Math.min(1, sp * 3);
  const amp   = H * 0.14;
  const steps = 140;
  const width = W * 0.72;
  const x0    = cx - width / 2;

  ctx.strokeStyle = `rgba(255,255,255,${(alpha * 0.9).toFixed(3)})`;
  ctx.lineWidth = 1.4 * dpr;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i < steps; i++) {
    const x = x0 + (i / (steps - 1)) * width;
    const nx = i / steps;
    const y = cy + (
      Math.sin(nx * 12 + t * 2.4) * 0.6 +
      Math.sin(nx * 24 + t * 1.7) * 0.35 +
      Math.sin(nx * 40 + t * 3.1) * 0.15
    ) * amp;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();

  // Axis reference marks
  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.28})`;
  for (let i = 0; i <= 6; i++) {
    const x = x0 + (i / 6) * width;
    ctx.fillRect(x, cy + H * 0.24, 1 * dpr, 4 * dpr);
  }
}

function drawEmbedding(ctx, cx, cy, W, H, dpr, t, sp) {
  const alpha = Math.min(1, sp * 3);
  const dotCount = 180;
  const radius = Math.min(W, H) * 0.28;

  ctx.fillStyle = `rgba(255,255,255,${(alpha * 0.8).toFixed(3)})`;
  for (let i = 0; i < dotCount; i++) {
    const theta = (i / dotCount) * Math.PI * 2;
    const jitter =
      Math.sin(theta * 5 + t * 0.9) * 0.14 +
      Math.sin(theta * 9 + t * 0.5) * 0.09;
    const r = radius * (1 + jitter);
    const rot = theta + t * 0.18;
    const x = cx + Math.cos(rot) * r;
    const y = cy + Math.sin(rot) * r;
    ctx.beginPath();
    ctx.arc(x, y, 1.1 * dpr, 0, Math.PI * 2);
    ctx.fill();
  }
  // Inner soft glow
  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.04})`;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.45, 0, Math.PI * 2);
  ctx.fill();
  // Core dot
  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.9})`;
  ctx.beginPath();
  ctx.arc(cx, cy, 2 * dpr, 0, Math.PI * 2);
  ctx.fill();
}

function drawTranslation(ctx, cx, cy, W, H, dpr, t, sp) {
  const LANGS = ['ENGLISH', 'ESPAÑOL', 'FRANÇAIS', '日本語', '中文', 'DEUTSCH', 'हिन्दी'];
  const alpha = Math.min(1, sp * 3);

  const cycle = 1.15;
  const idx = Math.floor(t / cycle) % LANGS.length;
  const raw = (t % cycle) / cycle;
  const eased = raw < 0.5 ? 2 * raw * raw : -1 + (4 - 2 * raw) * raw;

  ctx.font = `700 ${26 * dpr}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const yOffset = 32 * dpr;
  const currentY = cy - eased * yOffset;
  const nextY    = cy + (1 - eased) * yOffset;

  ctx.fillStyle = `rgba(255,255,255,${alpha * (1 - eased) * 0.95})`;
  ctx.fillText(LANGS[idx], cx, currentY);
  ctx.fillStyle = `rgba(255,255,255,${alpha * eased * 0.95})`;
  ctx.fillText(LANGS[(idx + 1) % LANGS.length], cx, nextY);

  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.12})`;
  ctx.lineWidth = 1 * dpr;
  const lineW = W * 0.32;
  [cy - 42 * dpr, cy + 42 * dpr].forEach((y) => {
    ctx.beginPath();
    ctx.moveTo(cx - lineW / 2, y);
    ctx.lineTo(cx + lineW / 2, y);
    ctx.stroke();
  });

  // Arrow
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.35})`;
  ctx.lineWidth = 1.2 * dpr;
  ctx.beginPath();
  ctx.moveTo(cx, cy + 52 * dpr);
  ctx.lineTo(cx, cy + 68 * dpr);
  ctx.moveTo(cx - 5 * dpr, cy + 62 * dpr);
  ctx.lineTo(cx, cy + 68 * dpr);
  ctx.lineTo(cx + 5 * dpr, cy + 62 * dpr);
  ctx.stroke();
}

function drawProsody(ctx, cx, cy, W, H, dpr, t, sp) {
  const alpha = Math.min(1, sp * 3);
  const steps = 120;
  const width = W * 0.72;
  const x0    = cx - width / 2;
  const amp   = H * 0.18;

  // Reference baseline
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.1})`;
  ctx.lineWidth = 1 * dpr;
  ctx.setLineDash([3 * dpr, 4 * dpr]);
  ctx.beginPath();
  ctx.moveTo(x0, cy);
  ctx.lineTo(x0 + width, cy);
  ctx.stroke();
  ctx.setLineDash([]);

  // Pitch curve
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.9})`;
  ctx.lineWidth = 1.6 * dpr;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i < steps; i++) {
    const x = x0 + (i / (steps - 1)) * width;
    const nx = i / (steps - 1);
    const y = cy - (
      Math.sin(nx * Math.PI * 3.5 + t * 0.5) * 0.6 +
      Math.sin(nx * Math.PI * 7 + t * 0.35) * 0.2 +
      Math.sin(nx * Math.PI * 1.5 + t * 0.2) * 0.15
    ) * amp;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();

  // Sample markers
  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.55})`;
  for (let i = 0; i < 6; i++) {
    const nx = (i + 0.5) / 6;
    const x = x0 + nx * width;
    const y = cy - (
      Math.sin(nx * Math.PI * 3.5 + t * 0.5) * 0.6 +
      Math.sin(nx * Math.PI * 7 + t * 0.35) * 0.2
    ) * amp;
    ctx.beginPath();
    ctx.arc(x, y, 2 * dpr, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawEmotion(ctx, cx, cy, W, H, dpr, t, sp) {
  const alpha = Math.min(1, sp * 3);

  // Concentric pulsing rings
  for (let i = 0; i < 4; i++) {
    const pulse = (t * 0.55 + i * 0.25) % 1;
    const r = pulse * Math.min(W, H) * 0.34;
    const a = (1 - pulse) * alpha * 0.38;
    ctx.strokeStyle = `rgba(255,255,255,${a.toFixed(3)})`;
    ctx.lineWidth = 1 * dpr;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Central abstract face
  const arcR = Math.min(W, H) * 0.09;
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.9})`;
  ctx.lineWidth = 1.8 * dpr;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx, cy - arcR * 0.15, arcR, Math.PI * 0.12, Math.PI * 0.88);
  ctx.stroke();

  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.9})`;
  ctx.beginPath();
  ctx.arc(cx - arcR * 0.85, cy - arcR * 0.7, 2 * dpr, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx + arcR * 0.85, cy - arcR * 0.7, 2 * dpr, 0, Math.PI * 2);
  ctx.fill();

  // Waveform arc below
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.4})`;
  ctx.lineWidth = 1 * dpr;
  const steps = 60;
  const wave_w = W * 0.5;
  const wave_x = cx - wave_w / 2;
  const wave_y = cy + arcR * 2.2;
  ctx.beginPath();
  for (let i = 0; i < steps; i++) {
    const nx = i / (steps - 1);
    const x = wave_x + nx * wave_w;
    const y = wave_y + Math.sin(nx * 20 + t * 3) * 6 * dpr;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
}

function drawLipSync(ctx, cx, cy, W, H, dpr, t, sp) {
  const alpha = Math.min(1, sp * 3);
  const cols = 20;
  const rows = 14;
  const gridW = W * 0.6;
  const gridH = H * 0.44;
  const dx = gridW / (cols - 1);
  const dy = gridH / (rows - 1);
  const x0 = cx - gridW / 2;
  const y0 = cy - gridH / 2;

  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.42})`;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = x0 + c * dx;
      const y = y0 + r * dy;
      const nx = (c / (cols - 1)) - 0.5;
      const ny = (r / (rows - 1)) - 0.5;
      const dist = Math.sqrt(nx * nx + ny * ny);
      const warp = Math.sin(t * 2.4) * Math.max(0, 1 - dist * 2) * 10 * dpr;
      ctx.beginPath();
      ctx.arc(x, y + warp * ny * 2, 0.9 * dpr, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Mouth ellipse opening/closing
  const openness = (Math.sin(t * 3.4) + 1) / 2;
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.9})`;
  ctx.lineWidth = 1.5 * dpr;
  ctx.lineCap = 'round';
  const mouthW = 58 * dpr;
  const mouthH = 4 * dpr + openness * 22 * dpr;
  ctx.beginPath();
  ctx.ellipse(cx, cy + 18 * dpr, mouthW / 2, mouthH / 2, 0, 0, Math.PI * 2);
  ctx.stroke();

  // Corner registration marks
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.35})`;
  ctx.lineWidth = 1 * dpr;
  const box = 26 * dpr;
  [[x0, y0], [x0 + gridW, y0], [x0, y0 + gridH], [x0 + gridW, y0 + gridH]].forEach(([bx, by]) => {
    ctx.beginPath();
    ctx.arc(bx, by, 3 * dpr, 0, Math.PI * 2);
    ctx.stroke();
  });
}

function drawOutput(ctx, cx, cy, W, H, dpr, t, sp, isOutro) {
  const alpha = Math.min(1, sp * 3);
  const baseR = Math.min(W, H) * 0.24;
  const outroBoost = isOutro ? 1.35 : 1;
  const breathe = 1 + Math.sin(t * 1.6) * 0.06;
  const r = baseR * breathe * outroBoost;

  // Halos
  for (let i = 5; i > 0; i--) {
    const rr = r * (1 + i * 0.16);
    const a = alpha * 0.06 * (1 - i / 5);
    ctx.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(cx, cy, rr, 0, Math.PI * 2);
    ctx.fill();
  }
  // Core sphere
  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.13})`;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  // Outline
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.55})`;
  ctx.lineWidth = 1.5 * dpr;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();

  // Waveform running through the sphere
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.7})`;
  ctx.lineWidth = 1.4 * dpr;
  ctx.lineCap = 'round';
  const steps = 80;
  const width = r * 1.7;
  const x0 = cx - width / 2;
  ctx.beginPath();
  for (let i = 0; i < steps; i++) {
    const nx = i / (steps - 1);
    const x = x0 + nx * width;
    // Fade at edges of sphere
    const edgeFade = Math.sin(nx * Math.PI);
    const y = cy + Math.sin(nx * 14 + t * 3) * 8 * dpr * edgeFade;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();

  // "READY" text once outro engages
  if (isOutro || sp > 0.55) {
    const readyAlpha = isOutro ? alpha : Math.min(1, (sp - 0.55) / 0.3);
    ctx.fillStyle = `rgba(255,255,255,${readyAlpha * 0.9})`;
    ctx.font = `700 ${14 * dpr}px 'Inter', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('READY', cx, cy + r + 26 * dpr);
  }
}
